import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import CustomizedSwitch from '../../../../components/Switch';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import MasterLookupService from '../../../../services/master-lookup.service';
import { Modules, AuthRoutes } from '../../../../enums';

interface BillingCycleFormData {
  cycle_code: string;
  cycle_name: string;
  duration_in_months: number | string;
  status: 'active' | 'inactive';
  notes: string;
}

const initialForm: BillingCycleFormData = {
  cycle_code: '',
  cycle_name: '',
  duration_in_months: '',
  status: 'active',
  notes: '',
};

const BillingCycleForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;

  const [formData, setFormData] = useState<BillingCycleFormData>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    MasterLookupService.get('billingCycles', id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          cycle_code: row.cycle_code ?? '',
          cycle_name: row.cycle_name ?? '',
          duration_in_months: row.duration_in_months ?? '',
          status: row.status ?? 'active',
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: BillingCycleFormData) => {
      const nextErrors: Record<string, string> = {};
      if (!data.cycle_code.trim()) nextErrors.cycle_code = t('masters.billingCycles.form.pageTexts.fields.cycleCode.errors.required');
      if (!data.cycle_name.trim()) nextErrors.cycle_name = t('masters.billingCycles.form.pageTexts.fields.cycleName.errors.required');
      const duration = Number(data.duration_in_months);
      if (!data.duration_in_months || !Number.isInteger(duration) || duration <= 0) {
        nextErrors.duration_in_months = t('masters.billingCycles.form.pageTexts.fields.durationInMonths.errors.invalid');
      }
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    const value = name === 'status' ? (e.target.checked ? 'active' : 'inactive') : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = { ...formData, duration_in_months: Number(formData.duration_in_months) };

        if (edit && id) {
          await MasterLookupService.update('billingCycles', id, payload);
          toast.success(<Toast message={t('masters.billingCycles.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await MasterLookupService.create('billingCycles', payload);
          toast.success(<Toast message={t('masters.billingCycles.form.pageTexts.addSuccess')} />, { icon: false });
        }
        navigate(`${Modules.masters}${AuthRoutes.billingCycles}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.billingCycles.form.pageTexts.editTitle') : t('masters.billingCycles.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-4">
          <Input
            id="cycle_code"
            name="cycle_code"
            label={t('masters.billingCycles.form.pageTexts.fields.cycleCode.label')}
            placeholder={t('masters.billingCycles.form.pageTexts.fields.cycleCode.placeholder')}
            required
            value={formData.cycle_code}
            error={!!errors.cycle_code}
            errorMessage={errors.cycle_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="cycle_name"
            name="cycle_name"
            label={t('masters.billingCycles.form.pageTexts.fields.cycleName.label')}
            placeholder={t('masters.billingCycles.form.pageTexts.fields.cycleName.placeholder')}
            required
            value={formData.cycle_name}
            error={!!errors.cycle_name}
            errorMessage={errors.cycle_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="duration_in_months"
            name="duration_in_months"
            type="number"
            min="1"
            label={t('masters.billingCycles.form.pageTexts.fields.durationInMonths.label')}
            placeholder={t('masters.billingCycles.form.pageTexts.fields.durationInMonths.placeholder')}
            required
            value={formData.duration_in_months}
            error={!!errors.duration_in_months}
            errorMessage={errors.duration_in_months}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <CustomizedSwitch
            label={t('masters.billingCycles.form.pageTexts.fields.status.label')}
            name="status"
            checked={formData.status === 'active'}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-8">
          <Input
            id="notes"
            name="notes"
            label={t('masters.billingCycles.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.billingCycles.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.billingCycles.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.billingCycles}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.billingCycles.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
    </div>
  );
};

export default BillingCycleForm;
